import React, { FC, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  FormActionFooter,
  Row,
  Column,
  FormNavPrompt,
  HRule,
  RadioGroup,
  FormInput,
} from 'Shared/components';
import { DatePickerModal, formatDatePickerDate } from 'QuoteApp/components';
import { useCommissionsContext } from '../../services';
import styles from './styles.module.css';

export enum CommissionStructure {
  Commission = 'commission',
  Bid = 'bid',
  Flat = 'flat_per_sale',
}

export type CommissionFormType = {
  effectiveDate: string;
  endDate?: string;
  commissionStructure: CommissionStructure;
  commissionTemplate: string;
};

export const commissionStructureTextMap: Record<CommissionStructure, string> = {
  [CommissionStructure.Commission]:
    'Please choose a pre-defined commission template.',
  [CommissionStructure.Flat]:
    'Sales commission is stored in PipeDrive. Please visit PipeDrive to verify amount.',
  [CommissionStructure.Bid]:
    'Sales commission is variable based on the output of ClearAI model, policy holder characteristics, and business appetite.',
};

export const commissionStructureOptions: Array<{
  label: string;
  value: CommissionStructure;
}> = [
  { label: 'Commission', value: CommissionStructure.Commission },
  { label: 'Flat per sale', value: CommissionStructure.Flat },
  { label: 'Bid', value: CommissionStructure.Bid },
];

export const CommissionForm: FC<{ type: 'create' | 'readonly' }> = ({
  type,
}) => {
  const history = useHistory();
  const { templateData } = useCommissionsContext();
  const {
    dirty,
    errors,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<CommissionFormType>();

  const onCancel = useCallback(
    () => history.push(history.location.pathname.replace('/create', '')),
    [history]
  );

  const selectedTemplate = templateData.templates[values.commissionTemplate];

  return (
    <form onSubmit={handleSubmit} data-testid={`${type}-commission-form`}>
      <FormNavPrompt when={dirty} />
      <h3>Details</h3>
      <Row>
        <Column variant="half">
          <DatePickerModal
            modalHeader="Commission Effective Date"
            label="Effective date"
            name="effectiveDate"
            startDate={
              values.effectiveDate ? new Date(values.effectiveDate) : undefined
            }
            onDateChange={date => {
              setFieldValue('effectiveDate', formatDatePickerDate(date));
            }}
            errorMessage={
              touched.effectiveDate ? errors.effectiveDate : undefined
            }
            disabled={isSubmitting || type === 'readonly'}
          />
        </Column>
        <Column variant="half">
          <DatePickerModal
            modalHeader="Commission End Date"
            label="End date"
            name="endDate"
            startDate={values.endDate ? new Date(values.endDate) : undefined}
            minDateAllowed={
              values.effectiveDate ? new Date(values.effectiveDate) : undefined
            }
            onDateChange={date => {
              setFieldValue('endDate', formatDatePickerDate(date));
            }}
            errorMessage={errors.endDate ?? undefined}
            disabled={
              isSubmitting || type === 'readonly' || !values.effectiveDate
            }
            placeholder="Optional"
            isClearable={!!values.endDate}
            onClear={() => setFieldValue('endDate', '')}
          />
        </Column>
      </Row>
      <HRule size="lg" />
      <h3 id="commission-structure-radiogroup-label">Commission Structure</h3>
      <Row>
        <Column>
          <RadioGroup
            className={styles.radioGroup}
            name="commissionStructure"
            variant="inline"
            options={commissionStructureOptions}
            value={values.commissionStructure}
            onChange={(value: string) =>
              setFieldValue('commissionStructure', value)
            }
            disabled={isSubmitting || type === 'readonly'}
            labelId="commission-structure-radiogroup-label"
          />
        </Column>
      </Row>
      <p>{commissionStructureTextMap[values.commissionStructure]}</p>
      {values.commissionStructure === CommissionStructure.Commission && (
        <div data-testid="commission-template-section">
          <Row className={styles.inputRow}>
            <Column variant="half">
              <FormInput
                name="commissionTemplate"
                variant="select"
                options={templateData.options}
                value={values.commissionTemplate}
                onChange={(value: string) =>
                  setFieldValue('commissionTemplate', value)
                }
                label="Commission Template"
                errorMessage={
                  touched.commissionTemplate
                    ? errors.commissionTemplate
                    : undefined
                }
                disabled={isSubmitting || type === 'readonly'}
              />
            </Column>
            <Column />
          </Row>
          {selectedTemplate && (
            <table
              className={styles.table}
              data-testid="template-details-table"
            >
              <thead>
                <tr>
                  <th></th>
                  <th>Currently Insured & Homeowner</th>
                  <th>Currently Insured & Renter</th>
                  <th>Currently Uninsured</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.rowLabel}>New Business</td>
                  <td>
                    {selectedTemplate.newBusiness.currentlyInsuredHomeOwner}
                  </td>
                  <td>{selectedTemplate.newBusiness.currentlyInsuredRenter}</td>
                  <td>{selectedTemplate.newBusiness.currentlyUninsured}</td>
                </tr>
                <tr>
                  <td className={styles.rowLabel}>Renewal Business</td>
                  <td>{selectedTemplate.renewal.currentlyInsuredHomeOwner}</td>
                  <td>{selectedTemplate.renewal.currentlyInsuredRenter}</td>
                  <td>{selectedTemplate.renewal.currentlyUninsured}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
      {type === 'create' && (
        <FormActionFooter
          disabled={!dirty}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
        />
      )}
    </form>
  );
};
